import React, { useState } from "react";
import {
  Target,
  TrendingDown,
  ShieldCheck,
  ArrowRight,
  Download,
  CheckCircle2,
  BarChart3,
  LineChart,
  Zap,
  Mail,
  Box,
  Rocket,
  ChevronRight,
} from "lucide-react";

export default function App() {
  const [showFormModal, setShowFormModal] = useState(false);

  const openFormModal = () => {
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-[#003366] p-1.5 rounded-lg shadow-lg">
              <Target className="text-white w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <span className="font-black text-lg tracking-tighter text-[#003366] uppercase">
                DiyQsPro
              </span>
              <span className="text-[8px] uppercase tracking-[0.2em] font-bold text-slate-400 -mt-1">
                Predict • Optimize • Deliver
              </span>
            </div>
          </div>
          <button
            onClick={openFormModal}
            className="text-sm font-bold bg-slate-900 text-white px-5 py-2 rounded-full hover:bg-indigo-950 transition-all shadow-md"
          >
            Join Beta
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 lg:pt-48 lg:pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-black uppercase tracking-widest mb-8">
                <Zap className="w-3 h-3 fill-current" />
                The Future of Quantity Surveying
              </div>
              <h1 className="text-5xl lg:text-7xl font-black text-slate-900 leading-[0.95] mb-8 tracking-tighter">
                Predict the <span className="text-indigo-600 italic">Future</span> of Your Assets.
              </h1>
              <p className="text-xl text-slate-600 mb-10 leading-relaxed max-w-xl">
                Master the lifecycle with <strong>DiyQsPro</strong>. Our AI‑driven engine transforms static estimates into dynamic 25‑year ROI strategies in seconds.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openFormModal}
                  className="bg-[#003366] text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-indigo-950 transition-all shadow-xl shadow-blue-900/20 flex items-center justify-center gap-2"
                >
                  Join the Beta <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={openFormModal}
                  className="bg-white border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-2xl font-bold text-lg hover:border-slate-300 transition-all flex items-center justify-center gap-2"
                >
                  Download Guide <Download className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Illustration */}
            <div className="relative">
              <div className="absolute inset-0 bg-indigo-500/10 blur-[100px] -z-10 rounded-full" />
              <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-2xl p-8 relative overflow-hidden">
                <div className="flex items-center justify-between mb-12">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                      <BarChart3 className="w-6 h-6" />
                    </div>
                    <span className="font-bold text-sm text-slate-800">
                      Predictive ROI Engine
                    </span>
                  </div>
                  <div className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                    Live Analysis
                  </div>
                </div>

                <div className="space-y-8">
                  {[{ "label": "Year 1-5 (CapEx Focus)", "val": 100, "color": "bg-[#003366]" },
                  { "label": "Year 6-15 (OpEx Break-even)", "val": 65, "color": "bg-indigo-500" },
                  { "label": "Year 16-25 (Net Profit)", "val": 85, "color": "bg-green-500" }].map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                        <span>{item.label}</span>
                        <span className="text-slate-900">{item.val}% Optimized</span>
                      </div>
                      <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.val}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-12 grid grid-cols-3 gap-4 pt-8 border-t border-slate-100">
                  <div className="text-center">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                      Predict
                    </p>
                    <p className="font-black text-lg">25Y</p>
                  </div>
                  <div className="text-center border-x border-slate-100">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                      Optimize
                    </p>
                    <p className="font-black text-lg text-green-600">-22%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                      Deliver
                    </p>
                    <p className="font-black text-lg text-indigo-600">ROI+</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blueprint CTA */}
      <section
        id="blueprint"
        className="py-24 bg-slate-900 text-white overflow-hidden relative"
      >
        <div className="absolute top-0 right-0 p-24 opacity-5">
          <Target className="w-96 h-96" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
              <div className="w-[300px] h-[400px] bg-white rounded-2xl p-10 flex flex-col justify-between shadow-2xl rotate-3">
                <div>
                  <Target className="w-12 h-12 text-[#003366] mb-6" />
                  <h3 className="text-slate-900 text-3xl font-black leading-none mb-2">
                    DiyQsPro<br />BLUEPRINT
                  </h3>
                  <div className="h-1.5 w-10 bg-indigo-600 rounded-full mb-6" />
                  <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] leading-relaxed">
                    5 Steps to Predict and Slash Maintenance Costs
                  </p>
                </div>
                <div className="text-[10px] font-black text-slate-800 uppercase tracking-widest pt-6 border-t border-slate-100">
                  LCCA Compliant Guide
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-4xl lg:text-5xl font-black mb-8 leading-tight tracking-tighter">
                Get the "25-Year Blueprint" Free.
              </h2>
              <p className="text-slate-400 text-xl mb-10 leading-relaxed">
                We've synthesized 50 years of construction data into a 5‑step framework for mastering Whole Life Costing.
              </p>
              <ul className="space-y-4 mb-10">
                {["Calculate NPV for complex portfolios", "Identify \"Silent\" Profit Killers", "Automate maintenance schedules"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-indigo-400 shrink-0" />
                    <span className="font-medium text-lg text-slate-200">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={openFormModal}
                className="bg-white text-slate-900 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-slate-100 transition-all flex items-center gap-2"
              >
                Download PDF Guide <Download className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-black text-[#003366] mb-6 tracking-tighter">
            Secure Your Early Access.
          </h2>
          <p className="text-lg text-slate-600 mb-12">
            Join the DiyQsPro waitlist to get the 25‑Year Blueprint guide and be the first to access the AI Calculator when we go live.
          </p>

          {/* Official GetResponse Signup Form */}
          <div className="max-w-xl mx-auto">
            <getresponse-form form-id="ec3e83b0-38f4-4763-abcf-396dd0728dd5" e="1"></getresponse-form>
          </div>

          <p className="mt-8 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">
            Join 150+ QS Professionals Already Waiting
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 border-t border-slate-100 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Target className="w-6 h-6 text-[#003366]" />
            <span className="font-black text-[#003366] text-2xl tracking-tighter uppercase">
              DiyQsPro
            </span>
          </div>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.4em] mb-12">
            Predict • Optimize • Deliver
          </p>
          <div className="flex justify-center gap-10 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <a href="#" className="hover:text-indigo-600 transition-colors">LinkedIn</a>
            <a href="#" className="hover:text-indigo-600 transition-colors">Privacy</a>
            <a href="#" className="hover:text-indigo-600 transition-colors">Terms</a>
            <a href="#" className="hover:text-indigo-600 transition-colors">Press</a>
          </div>
          <div className="mt-16 text-[10px] font-bold text-slate-300">
            © {new Date().getFullYear()} DiyQsPro Technologies. All rights reserved.
          </div>
        </div>
      </footer>

      {/* GetResponse Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-8 relative my-8 max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeFormModal}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-slate-400 hover:text-slate-600 transition-colors z-10 bg-white rounded-full p-1"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-4 sm:mb-6">
              <div className="bg-[#003366] p-2 sm:p-3 rounded-lg inline-block mb-3 sm:mb-4">
                <Target className="text-white w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-2 pr-8">Get Your Free Blueprint</h3>
              <p className="text-slate-600 text-xs sm:text-sm">Sign up to download the 25-Year Blueprint guide</p>
            </div>

            {/* Official GetResponse Form */}
            <getresponse-form form-id="ec3e83b0-38f4-4763-abcf-396dd0728dd5" e="1"></getresponse-form>
          </div>
        </div>
      )}
    </div>
  );
}
